import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ThumbsUp, ChatCircle, Trash, PaperPlaneRight, ChatsCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (isNaN(s)) return "";
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return m + "m";
  const h = Math.floor(m / 60);
  if (h < 24) return h + "h";
  const d = Math.floor(h / 24);
  if (d < 7) return d + "d";
  return new Date(iso).toLocaleDateString("en", { day: "numeric", month: "short" });
}

function Avatar({ id, name }) {
  if (id) return <img src={fileUrl(id)} alt={name} className="h-9 w-9 shrink-0 rounded-full object-cover" />;
  const letter = (name || "?").charAt(0).toUpperCase();
  return <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-sm font-medium text-primary">{letter}</span>;
}

function Bubble({ c, user, eventId, onChange, isReply }) {
  const canDelete = user && (user.id === c.user_id || user.role === "admin");
  const like = async () => {
    if (!user) { toast.error("Sign in to like"); return; }
    try { await api.post("/events/comments/" + c.id + "/like"); onChange(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response ? err.response.data.detail : null)); }
  };
  const del = async () => {
    try { await api.delete("/events/comments/" + c.id); toast.success("Deleted"); onChange(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response ? err.response.data.detail : null)); }
  };
  const likeClass = "font-medium transition-colors hover:text-secondary " + (c.my_like ? "text-secondary" : "");
  return (
    <div>
      <div className="inline-block rounded-2xl bg-muted/60 px-4 py-2">
        <div className="text-sm font-medium text-foreground">{c.user_name}</div>
        <div className="whitespace-pre-line text-sm text-foreground/90">{c.text}</div>
      </div>
      <div className="mt-1 flex items-center gap-4 pl-1 text-xs text-muted-foreground">
        <button onClick={like} className={likeClass} data-testid={"comment-like-" + c.id}>
          <ThumbsUp size={13} weight={c.my_like ? "fill" : "regular"} className="mr-1 inline" />
          Like{c.like_count ? " · " + c.like_count : ""}
        </button>
        <span>{timeAgo(c.created_at)}</span>
        {canDelete ? <button onClick={del} className="hover:text-destructive" data-testid={"comment-delete-" + c.id}><Trash size={13} /></button> : null}
      </div>
    </div>
  );
}

function TopComment({ c, user, eventId, onChange }) {
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState("");
  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post("/events/" + eventId + "/comments", { text: reply, parent_id: c.id });
      setReply(""); setReplying(false); onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response ? err.response.data.detail : null)); }
  };
  return (
    <div className="flex gap-3" data-testid={"comment-" + c.id}>
      <Avatar id={c.avatar_file_id} name={c.user_name} />
      <div className="min-w-0 flex-1">
        <Bubble c={c} user={user} eventId={eventId} onChange={onChange} />
        <button onClick={() => setReplying(!replying)} className="mt-1 pl-1 text-xs font-medium text-muted-foreground hover:text-secondary" data-testid={"comment-reply-" + c.id}>
          <ChatCircle size={13} className="mr-1 inline" />Reply
        </button>
        {replying ? (
          <div className="mt-2 flex gap-2">
            <Textarea rows={1} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={"Reply to " + c.user_name} className="min-h-[38px] resize-none rounded-2xl" data-testid={"reply-input-" + c.id} />
            <Button size="icon" className="rounded-full" onClick={sendReply} data-testid={"reply-send-" + c.id}><PaperPlaneRight size={16} /></Button>
          </div>
        ) : null}
        {c.replies && c.replies.length > 0 ? (
          <div className="mt-3 space-y-3 border-l border-border/60 pl-4">
            {c.replies.map((r) => (
              <div key={r.id} className="flex gap-3" data-testid={"comment-" + r.id}>
                <Avatar id={r.avatar_file_id} name={r.user_name} />
                <div className="min-w-0 flex-1"><Bubble c={r} user={user} eventId={eventId} onChange={onChange} isReply /></div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function EventDiscussion({ eventId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ comments: [], total: 0 });
  const [text, setText] = useState("");
  const load = () => api.get("/public/events/" + eventId + "/comments").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const post = async () => {
    if (!text.trim()) return;
    try { await api.post("/events/" + eventId + "/comments", { text }); setText(""); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response ? err.response.data.detail : null)); }
  };

  return (
    <section className="mt-10" data-testid="event-discussion">
      <h2 className="flex items-center gap-2 font-heading text-2xl font-light">
        <ChatsCircle size={22} weight="duotone" className="text-secondary" /> Discussion
        {data.total > 0 ? <span className="text-base text-muted-foreground">({data.total})</span> : null}
      </h2>

      {user ? (
        <div className="mt-4 flex gap-3">
          <Avatar id={null} name={user.name} />
          <div className="flex-1">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Share a memory, ask a question or say hello…" className="resize-none rounded-2xl" data-testid="comment-input" />
            <div className="mt-2 flex justify-end">
              <Button onClick={post} className="rounded-full gap-1.5" data-testid="comment-submit"><PaperPlaneRight size={15} /> Post</Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-border/60 bg-muted/30 p-5 text-center text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-secondary hover:underline">Sign in</Link> to join the discussion.
        </div>
      )}

      <div className="mt-6 space-y-5">
        {data.comments.length === 0 ? <p className="text-sm text-muted-foreground">No comments yet. Start the conversation!</p> : null}
        {data.comments.map((c) => <TopComment key={c.id} c={c} user={user} eventId={eventId} onChange={load} />)}
      </div>
    </section>
  );
}
